
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useUIStore from '@/app/store/ui.store';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTenant } from '@/shared/hooks/useTenant';
import { useTranslation } from '@/shared/i18n';
import { supportedLanguages, Language } from '@/shared/i18n/config';
import { cn } from '@/shared/utils/helpers/cn';

// Icons
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  BellIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  ChevronDownIcon,
  GlobeAltIcon,
  LanguageIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const { setSidebarOpen, theme, setTheme } = useUIStore();
  const { user, logout } = useAuth();
  const { currentTenant, availableTenants, switchTenant } = useTenant();
  const { t, i18n } = useTranslation();
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside.
  // Use 'click' (not 'mousedown') so child onClick handlers fire before the close —
  // sinon le mousedown pouvait fermer le panel + retirer le bouton du DOM avant
  // que l'événement click natif n'atteigne <button onClick={logout}>.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Logout bullet-proof : on stoppe d'abord la propagation pour ne pas
  // déclencher le close-on-outside, puis on clear le state, puis on force
  // une navigation hard (window.location.replace) qui marche même si le
  // composant Router était démonté entre-temps. navigate(...) reste comme
  // tentative préférée pour garder l'historique propre.
  const handleLogout = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsUserMenuOpen(false);
    try {
      logout();
    } catch {
      /* ignore — on continue la nav même si le store fait un truc bizarre */
    }
    try {
      navigate('/login', { replace: true });
    } catch {
      /* ignore */
    }
    // Fallback dur — garantit la sortie même si la SPA route ne réagit pas.
    setTimeout(() => {
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    }, 50);
  };

  const changeLanguage = (lng: Language) => {
    i18n.changeLanguage(lng);
  };

  const getCurrentLanguage = (): Language => {
    return (i18n.language as Language) || 'fr';
  };

  const handleThemeToggle = () => {
    const themes = ['light', 'dark', 'system'] as const;
    const currentIndex = themes.indexOf(theme);
    const nextTheme = themes[(currentIndex + 1) % themes.length];
    setTheme(nextTheme);
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return <SunIcon className="w-5 h-5" />;
      case 'dark':
        return <MoonIcon className="w-5 h-5" />;
      case 'system':
        return <ComputerDesktopIcon className="w-5 h-5" />;
      default:
        return <SunIcon className="w-5 h-5" />;
    }
  };

  return (
    <header className="bg-white/[0.04] backdrop-blur-xl border-b border-white/[0.06] h-14 flex items-center justify-between px-4 lg:px-6">
      {/* Left Section */}
      <div className="flex items-center space-x-4">
        {/* Mobile menu button */}
        <button
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden p-2 rounded-md text-white/50 hover:text-white/80 hover:bg-white/[0.06]"
        >
          <Bars3Icon className="w-5 h-5" />
        </button>

        {/* Search */}
        <div className="hidden md:flex items-center">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-neutral-400" />
            </div>
            <input
              type="text"
              placeholder={t('header.searchPlaceholder')}
              className="block w-80 pl-10 pr-3 py-2 border border-white/[0.10] rounded-lg leading-5 bg-white/[0.06] text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm"
            />
          </div>
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center space-x-4">
        {/* Language Selector */}
        <div className="relative" ref={languageDropdownRef}>
          <button
            onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white/60 bg-white/[0.06] rounded-lg hover:bg-white/[0.10] transition-colors duration-200"
          >
            <LanguageIcon className="w-4 h-4" />
            <span className="hidden sm:inline">
              {supportedLanguages.find(lang => lang.code === getCurrentLanguage())?.code.toUpperCase()}
            </span>
            <ChevronDownIcon className="w-4 h-4" />
          </button>

          {/* Language Dropdown */}
          {isLanguageDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-[#0d2847]/95 backdrop-blur-xl rounded-xl shadow-lg py-1 z-50 border border-white/[0.10]">
              {supportedLanguages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => {
                    changeLanguage(language.code);
                    setIsLanguageDropdownOpen(false);
                  }}
                  className={cn(
                    'block w-full text-left px-4 py-2 text-sm transition-colors duration-200',
                    getCurrentLanguage() === language.code
                      ? 'bg-blue-500/20 text-blue-300'
                      : 'text-white/60 hover:bg-white/[0.06]'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span>{language.nativeName}</span>
                    <span className="text-xs text-white/40">
                      {language.code.toUpperCase()}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Tenant Selector */}
        {availableTenants.length > 1 && (
          <div className="relative">
            <button className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white/60 bg-white/[0.06] rounded-lg hover:bg-white/[0.10] transition-colors duration-200">
              <GlobeAltIcon className="w-4 h-4" />
              <span className="hidden sm:inline">{currentTenant?.name || t('layout.header.tenantFallback')}</span>
              <ChevronDownIcon className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Theme Toggle */}
        <button
          onClick={handleThemeToggle}
          className="p-2 rounded-lg text-white/40 hover:text-white/70 hover:bg-white/[0.06] transition-colors duration-200"
          title={t('header.theme.current', { mode: t(`header.theme.${theme}`) })}
        >
          {getThemeIcon()}
        </button>

        {/* Notifications */}
        <button className="relative p-2 rounded-lg text-white/40 hover:text-white/70 hover:bg-white/[0.06] transition-colors duration-200">
          <BellIcon className="w-5 h-5" />
          {/* Notification badge */}
          <span className="absolute top-1 right-1 w-2 h-2 bg-error-500 rounded-full"></span>
        </button>

        {/* User Menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-white/70 hover:bg-white/[0.06] transition-colors duration-200"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-400 rounded-full flex items-center justify-center shadow-lg shadow-blue-600/20">
              <span className="text-white font-medium text-sm">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="hidden lg:block text-left">
              <p className="text-sm font-medium text-white">
                {user?.name || t('layout.header.userFallback')}
              </p>
              <p className="text-xs text-white/40">
                {user?.level || t('layout.header.levelFallback')}
              </p>
            </div>
            <ChevronDownIcon className="w-4 h-4" />
          </button>

          {/* User Dropdown */}
          {isUserMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-[#0d2847]/95 backdrop-blur-xl rounded-xl shadow-lg py-1 z-50 border border-white/[0.10]">
              {/* User Info */}
              <div className="px-4 py-3 border-b border-white/[0.08]">
                <p className="text-sm font-medium text-white">
                  {user?.name || t('layout.header.userFallback')}
                </p>
                <p className="text-xs text-white/40 mt-1">
                  {user?.email || 'user@example.com'}
                </p>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-white/60 hover:bg-white/[0.06] transition-colors duration-200"
                >
                  <UserCircleIcon className="w-4 h-4 mr-3" />
                  {t('layout.header.profile')}
                </button>

                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors duration-200"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4 mr-3" />
                  {t('layout.header.logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
